import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  BarChart3,
  Users,
  Target,
  AlertCircle,
  Loader2,
  TrendingUp,
  CheckCircle
} from 'lucide-react';
import { getSkillHeatmap, getEmployerJobs } from '../../services/api';

const SkillHeatmap = () => {
  const [heatmapData, setHeatmapData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    fetchHeatmap();
  }, [selectedJob]);

  const fetchJobs = async () => {
    try {
      const res = await getEmployerJobs();
      setJobs(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchHeatmap = async () => {
    setLoading(true);
    try {
      const res = await getSkillHeatmap(selectedJob || undefined);
      setHeatmapData(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'high': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getAvailabilityLabel = (availability) => {
    switch (availability) {
      case 'high': return 'Hoch';
      case 'medium': return 'Mittel';
      case 'low': return 'Niedrig';
      default: return 'Unbekannt';
    }
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="heatmap-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-primary" />
              Skill-Heatmap
            </h1>
            <p className="text-muted-foreground mt-1">
              Kompetenzverteilung im Kandidatenpool
            </p>
          </div>
          <Select value={selectedJob || "all"} onValueChange={(val) => setSelectedJob(val === "all" ? "" : val)}>
            <SelectTrigger className="w-64" data-testid="job-filter-select">
              <SelectValue placeholder="Alle Jobs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Jobs</SelectItem>
              {jobs.map(job => (
                <SelectItem key={job.job_id} value={job.job_id}>
                  {job.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : heatmapData ? (
          <div className="space-y-6">
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Kandidaten gesamt</p>
                      <p className="text-2xl font-bold">{heatmapData.total_candidates}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <Target className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Skills erfasst</p>
                      <p className="text-2xl font-bold">{heatmapData.skills?.length || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Anforderungs-Abdeckung</p>
                      <p className="text-2xl font-bold">{heatmapData.coverage?.coverage_percent || 0}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Coverage Gaps */}
            {heatmapData.coverage?.gaps?.length > 0 && (
              <Card className="border-red-200 bg-red-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-5 h-5" />
                    Kompetenzlücken
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {heatmapData.coverage.gaps.map((gap, idx) => (
                      <Badge key={idx} variant="destructive" className="flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {gap}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-sm text-red-600 mt-3">
                    Diese Kompetenzen sind in Ihrem Kandidatenpool nicht vorhanden.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Skills Heatmap */}
            <Card>
              <CardHeader>
                <CardTitle>Kompetenzverteilung</CardTitle>
              </CardHeader>
              <CardContent>
                {heatmapData.skills?.length > 0 ? (
                  <div className="space-y-4">
                    {heatmapData.skills.map((skill, idx) => (
                      <div key={idx} className="space-y-2" data-testid={`skill-${idx}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-sm">{skill.skill}</span>
                            {skill.is_required && (
                              <Badge variant="outline" className="text-xs border-primary text-primary">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Erforderlich
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground">
                              {skill.count} Kandidaten
                            </span>
                            <span className="text-sm text-muted-foreground">
                              Ø Level {skill.avg_level}/5
                            </span>
                            <div className="flex items-center gap-1">
                              <span className={`w-2 h-2 rounded-full ${getAvailabilityColor(skill.availability)}`} />
                              <span className="text-xs text-muted-foreground">
                                {getAvailabilityLabel(skill.availability)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={(skill.avg_level / 5) * 100}
                            className="flex-1 h-2"
                          />
                          <div className="w-16 h-6 rounded flex">
                            {[1, 2, 3, 4, 5].map(l => (
                              <div
                                key={l}
                                className={`flex-1 ${
                                  l <= skill.avg_level
                                    ? skill.avg_level >= 4 ? 'bg-green-400' : skill.avg_level >= 2.5 ? 'bg-yellow-400' : 'bg-red-400'
                                    : 'bg-gray-200'
                                } ${l === 1 ? 'rounded-l' : ''} ${l === 5 ? 'rounded-r' : ''}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      Keine Kompetenzdaten vorhanden. Bewerber müssen zuerst ihre Profile vervollständigen.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Keine Daten verfügbar</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default SkillHeatmap;
